import { VStack } from '@chakra-ui/layout'
import { Box, Divider, Text as ChakraText } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { BiVideo } from 'react-icons/bi'
import { GrImage as GrImageIcon } from 'react-icons/gr'
import {
  MdTransform,
  MdOutlinePermMedia,
  MdOutlineCloudUpload,
  MdSecurity,
} from 'react-icons/md'

import {IoMdCodeWorking} from 'react-icons/io'
import { FaUserLock, FaUserShield } from 'react-icons/fa'
import styled, { createGlobalStyle } from 'styled-components'
import { colors } from '../../../ui-kit'
import { SideBar } from '../../layouts/Base'
import { SideBarItem } from '../../layouts/Base/SideBar'
import { checkPermission, PermissionsEnum } from '../../../utils'
import { UserContext } from '../../providers'

const GrImage = styled(GrImageIcon)`
  path {
    stroke: ${props => props.stroke};
  }
`

const ActiveLinkStyles = createGlobalStyle`
  .active-link {
    ${GrImage} {
      path {
        stroke: ${colors.appSlate};
      }
    }

    :not(${GrImage}) {
      svg {
        fill: ${colors.appSlate};
      }
    }
    p {
      color: ${colors.appSlate};
    }
  }
`

const Menu = ({ children }: { children: React.ReactElement[] }) => {
  const { t } = useTranslation()
  return (
    <SideBar heading={t('settings', 'Settings')}>
      <ActiveLinkStyles />
      <VStack
        overflow="auto"
        h="100%"
        spacing="1em"
        padding="1.5em 0.75em 1.5em"
      >
        {React.Children.map(children, child => (
          <>{child}</>
        ))}
      </VStack>
    </SideBar>
  )
}

const MenuItem = ({
  permissions,
  children,
}: {
  permissions: PermissionsEnum[]
  children: any
}): React.ReactElement => {
  const { user } = React.useContext(UserContext)

  const hasPermission = permissions.find(permission =>
    checkPermission({ user, permission }),
  )
  if (hasPermission) {
    return (
      <>
        {children}
        <Divider />
      </>
    )
  }
  return <></>
}

const ExternalStorageSideMenu = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Menu>
      <MenuItem permissions={[PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem to="/settings/images" activeClassName="active-link">
          <Box margin="1rem">
            <GrImage fontSize="2rem" stroke={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_images', 'Images')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem to="/settings/videos" activeClassName="active-link">
          <Box margin="1rem">
            <BiVideo fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_video', 'Video')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem
          to="/settings/named-transforms"
          activeClassName="active-link"
        >
          <Box margin="1rem">
            <MdTransform fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_named_transforms', 'Named Transforms')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE, PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem to="/settings/media-library" activeClassName="active-link">
          <Box margin="1rem">
            <MdOutlinePermMedia
              fontSize="2rem"
              fill={colors.appBattleshipGrey}
            />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_media_library', 'Media Library')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem to="/settings/backup-bucket" activeClassName="active-link">
          <Box margin="1rem">
            <MdOutlineCloudUpload
              fontSize="2rem"
              fill={colors.appBattleshipGrey}
            />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_backup_bucket', 'Backup Bucket')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW]}>
        <SideBarItem
          to="/settings/advanced-security"
          activeClassName="active-link"
        >
          <Box margin="1rem">
            <MdSecurity fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_advanced_security', 'Advanced security')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_INTEGRATION_VIEW]}>
        <SideBarItem
          to="/settings/url-endpoint-functions"
          activeClassName="active-link"
        >
          <Box margin="1rem">
            <IoMdCodeWorking fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
             {t('url_endpoint_functions', 'URL-endpoint functions')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_TEAM_MANAGE]}>
        <SideBarItem
          to="/settings/multi-factor-auth"
          activeClassName="active-link"
        >
          <Box margin="1rem">
            <FaUserShield fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t(
              'global_multifactor_authentication',
              'Global multi-factor authentication',
            )}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
      <MenuItem permissions={[PermissionsEnum.SELF_TEAM_MANAGE]}>
        <SideBarItem
          to="/settings/single-sign-on"
          activeClassName="active-link"
        >
          <Box margin="1rem">
            <FaUserLock fontSize="2rem" fill={colors.appBattleshipGrey} />
          </Box>
          <ChakraText
            fontSize="md"
            fontWeight="500"
            color={colors.appBattleshipGrey}
            lineHeight="4rem"
          >
            {t('settings_single_sign_on', 'Single Sign On')}
          </ChakraText>
        </SideBarItem>
      </MenuItem>
    </Menu>
  )
}

export default ExternalStorageSideMenu
